import type { QueryClient } from '@tanstack/react-query';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import UserNotification from 'util/UserNotification';
import { useInvestigationDrawer } from 'security-app/components/Investigations/contexts/InvestigationDrawer';
import { useDashboardContext } from 'security-app/components/Investigations/Dashboard';
import type { ColumnFilterData } from 'security-app/components/common/Filters/ColumnFilter.types';
import type { EnableNotificationsResponse } from 'security-app/hooks/InvestigationsAPI.types';
import { defaultOnError } from 'util/conditional/onError';

import {
  fetchInvestigations,
  getInvestigationDetails,
  newInvestigation,
  updateInvestigations,
  archiveInvestigation,
  fetchPriorities,
  fetchStatuses,
  fetchUsers,
  fetchTeams,
  addNote,
  deleteNote,
  editNote,
  addEvidence,
  bulkAddEvidence,
  addPriority,
  updatePriority,
  reprioritizePriorities,
  deletePriority,
  addStatus,
  updateStatus,
  deleteStatus,
  removeEvidence,
  bulkArchiveInvestigation,
  deleteInvestigation,
  bulkDeleteInvestigations,
  setDefaultPriority,
  removeDefaultPriority,
  setDefaultStatus,
  removeDefaultStatus,
  enableNotifications,
  fetchInvestigationsConfig,
  fetchInvestigationsFilterOptions,
} from './api/investigationsAPI';
import type {
  UpdateInvestigationResponseAPIType,
  ConfigApiType,
  BulkAddEvidenceAPIType,
  PriorityAPIType,
} from './api/investigationsAPI.types';

export type PaginatedProps = {
  page: number;
  perPage: number;
  query?: string;
  orderBy?: string;
  direction?: 'asc' | 'desc';
  filters?: ColumnFilterData;
};

export function useGetInvestigations(
  { page, perPage, query = undefined, orderBy = undefined, direction = undefined, filters = undefined }: PaginatedProps,
  archived?: boolean,
  userTimezone?: string,
) {
  const { data, isLoading } = useQuery({
    queryKey: ['get-investigations', page, perPage, query, orderBy, direction, filters, userTimezone, archived],

    queryFn: () =>
      defaultOnError(
        fetchInvestigations(page, perPage, query, orderBy, direction, filters, archived, userTimezone),
        'Error fetching investigations',
      ),

    retry: 2,
    placeholderData: keepPreviousData,
  });

  return {
    loadingInvestigations: isLoading,
    investigations: isLoading ? [] : data?.investigations,
    pagination: {
      page: data?.page || page,
      perPage: data?.per_page || perPage,
      total: data?.total || 0,
      grandTotal: data?.grand_total || 0,
      count: data?.count || 0,
    },
  };
}

export function useFetchInvestigationsFilterOptions() {
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: fetchInvestigationsFilterOptions,
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return {
    fetchInvestigationsFilterOptions: mutateAsync,
    fetchingInvestigationsFilterOptions: isLoading,
  };
}

export function useGetInvestigationDetails(invId: string) {
  const { selectedInvestigationId, setSelectedInvestigationId } = useInvestigationDrawer();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['get-investigation-details', invId],

    queryFn: () => {
      try {
        return getInvestigationDetails(invId);
      } catch (error) {
        if (selectedInvestigationId === invId) {
          setSelectedInvestigationId(undefined);
          UserNotification.warning('The investigation you were viewing has been deleted');
        } else if (error.status === 404) {
          UserNotification.error(
            `Unable to find Investigation with id <${invId}>, please ensure it wasn't deleted.`,
            'Could not retrieve Investigation',
          );
        } else {
          UserNotification.error(error.message);
        }

        throw error;
      }
    },

    retry: 2,
    enabled: !!invId,
  });

  return {
    loadingInvestigationDetails: isLoading,
    fetchError: isError,
    currentInvestigation: data,
  };
}

export function useNewInvestigation() {
  const queryClient = useQueryClient();
  const { forceRefreshDashboard } = useDashboardContext();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: newInvestigation,
    onSuccess: () => {
      UserNotification.success('New Investigation created');
      queryClient.invalidateQueries({
        queryKey: ['get-investigations'],
      });
      forceRefreshDashboard();
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { createInvestigation: mutateAsync, creatingInvestigation: isLoading };
}

export function useUpdateInvestigations() {
  const queryClient = useQueryClient();
  const { forceRefreshDashboard } = useDashboardContext();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateInvestigations,
    onSuccess: (data: UpdateInvestigationResponseAPIType) => {
      if (data.errors.length > 0) {
        const error = { type: 'warning', message: `${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success('Investigations updated successfully');
      }

      queryClient.invalidateQueries({
        queryKey: ['get-investigation-details'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-investigations'],
      });
      forceRefreshDashboard();
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return {
    updateInvestigations: mutateAsync,
    updatingInvestigations: isLoading,
  };
}

export function useArchiveInvestigation() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: archiveInvestigation,
    onSuccess: () => {
      UserNotification.success('Investigation archive status updated successfully');
      queryClient.invalidateQueries({
        queryKey: ['get-investigation-details'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-investigations'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return {
    archiveInvestigation: mutateAsync,
    archivingInvestigation: isLoading,
  };
}

export function useBulkArchiveInvestigation() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkArchiveInvestigation,
    onSuccess: () => {
      UserNotification.success('Investigations archive status updated successfully');
      queryClient.invalidateQueries({
        queryKey: ['get-investigation-details'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-investigations'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return {
    bulkArchiveInvestigation: mutateAsync,
    archivingInvestigations: isLoading,
  };
}

export function useDeleteInvestigation() {
  const queryClient = useQueryClient();
  const { forceRefreshDashboard } = useDashboardContext();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteInvestigation,
    onSuccess: () => {
      UserNotification.success('Investigation deleted successfully');
      queryClient.invalidateQueries({
        queryKey: ['get-investigations'],
      });
      forceRefreshDashboard();
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return {
    deleteInvestigation: mutateAsync,
    deletingInvestigation: isLoading,
  };
}

export function useBulkDeleteInvestigations() {
  const queryClient = useQueryClient();
  const { forceRefreshDashboard } = useDashboardContext();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkDeleteInvestigations,
    onSuccess: () => {
      UserNotification.success('Investigations deleted successfully');
      queryClient.invalidateQueries({
        queryKey: ['get-investigations'],
      });
      forceRefreshDashboard();
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return {
    bulkDelete: mutateAsync,
    bulkDeleting: isLoading,
  };
}

export function useAddEvidence() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addEvidence,
    onSuccess: async (data: UpdateInvestigationResponseAPIType) => {
      if (data.errors.length > 0) {
        const error = { type: 'warning', message: `${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success('Evidence added to investigation');
      }

      await new Promise((resolve: (arg?: any) => void) => {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: ['get-investigation-details'],
          });
          resolve();
        }, 1000);
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { addEvidence: mutateAsync, addingEvidence: isLoading };
}

const invalidateEntities = (queryClient: QueryClient, payload: BulkAddEvidenceAPIType) => {
  const investigations = payload?.investigation_ids;
  const events = payload?.events;

  if (investigations?.length) {
    investigations.forEach((investigation) =>
      queryClient.invalidateQueries({
        queryKey: ['get-investigation-details', investigation],
      }),
    );
  }
  if (events?.length) {
    events.forEach((event) =>
      queryClient.invalidateQueries({
        queryKey: ['get-security-events', event],
      }),
    );
  }
};
export function useBulkAddEvidence() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkAddEvidence,
    onSuccess: async (data, variables) => {
      if (data.errors.length > 0) {
        const error = { type: 'warning', message: `${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success('Evidence added to investigation');
      }

      await new Promise((resolve: (arg?: any) => void) => {
        setTimeout(() => {
          invalidateEntities(queryClient, variables.payload);

          resolve();
        }, 1000);
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { bulkAddEvidence: mutateAsync, addingEvidence: isLoading };
}

export function useRemoveEvidence() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: removeEvidence,
    onSuccess: async () => {
      UserNotification.success('Evidence removed from investigation');

      await new Promise((resolve: (arg?: any) => void) => {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: ['get-investigation-details'],
          });
          resolve();
        }, 1000);
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { removeEvidence: mutateAsync, removingEvidence: isLoading };
}

export function useAddNote() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addNote,
    onSuccess: () => {
      UserNotification.success('New note added to investigation');
      queryClient.invalidateQueries({
        queryKey: ['get-investigation-details'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { addNote: mutateAsync, addingNote: isLoading };
}

export function useEditNote() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: editNote,
    onSuccess: () => {
      UserNotification.success('Note edited');
      queryClient.invalidateQueries({
        queryKey: ['get-investigation-details'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { editNote: mutateAsync, editingNote: isLoading };
}

export function useDeleteNote() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: deleteNote,
    onSuccess: () => {
      UserNotification.success('Note deleted from investigation');
      queryClient.invalidateQueries({
        queryKey: ['get-investigation-details'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { deleteNote: mutateAsync };
}

export function useGetPriorities(execute: boolean = true) {
  const { data, isLoading } = useQuery({
    queryKey: ['get-all-priorities'],
    queryFn: () => defaultOnError(fetchPriorities(), 'Error fetching priorities'),
    retry: 2,
    enabled: execute,
  });

  return {
    priorities: (data || []).sort((a: PriorityAPIType, b: PriorityAPIType) => a.priority - b.priority),
    loadingPriorities: isLoading,
  };
}

export function useGetStatuses(execute: boolean = true) {
  const { data, isLoading } = useQuery({
    queryKey: ['get-all-statuses'],
    queryFn: () => defaultOnError(fetchStatuses(), 'Error fetching statuses'),
    retry: 2,
    enabled: execute,
  });

  return {
    statuses: data || [],
    loadingStatuses: isLoading,
  };
}

export function useGetUsers(execute: boolean = true) {
  const { data, isLoading } = useQuery({
    queryKey: ['get-all-users'],
    queryFn: () => defaultOnError(fetchUsers(), 'Error fetching users'),
    retry: 2,
    enabled: execute,
  });

  const { users } = data || { users: [] };

  return {
    users: users,
    loadingUsers: isLoading,
  };
}

export function useGetTeams(execute: boolean = true) {
  const { data, isLoading } = useQuery({
    queryKey: ['get-all-teams'],
    queryFn: () => defaultOnError(fetchTeams(), 'Error fetching teams'),
    retry: 2,
    enabled: execute,
  });

  const { teams } = data || { teams: [] };

  return {
    teams,
    loadingTeams: isLoading,
  };
}

export function useAddPriority() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addPriority,
    onSuccess: () => {
      UserNotification.success('New priority tag added');
      queryClient.invalidateQueries({
        queryKey: ['get-all-priorities'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { addPriority: mutateAsync, addingPriority: isLoading };
}

export function useUpdatePriority() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updatePriority,
    onSuccess: () => {
      UserNotification.success('Priority tag updated');
      queryClient.invalidateQueries({
        queryKey: ['get-all-priorities'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-investigations'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-investigation-details'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { updatePriority: mutateAsync, updatingPriority: isLoading };
}

export function useReprioritizePriorities() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: reprioritizePriorities,
    onSuccess: () => {
      UserNotification.success('Priority tags updated');
      queryClient.invalidateQueries({
        queryKey: ['get-all-priorities'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-investigations'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-investigation-details'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { reprioritizePriorities: mutateAsync, reprioritizingPriorities: isLoading };
}

export function useDeletePriority() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deletePriority,
    onSuccess: () => {
      UserNotification.success('Priority tag deleted');
      queryClient.invalidateQueries({
        queryKey: ['get-all-priorities'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-investigations'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-investigation-details'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { deletePriority: mutateAsync, deletingPriority: isLoading };
}

export function useAddStatus() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addStatus,
    onSuccess: () => {
      UserNotification.success('New status tag added');
      queryClient.invalidateQueries({
        queryKey: ['get-all-statuses'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { addStatus: mutateAsync, addingStatus: isLoading };
}

export function useUpdateStatus() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateStatus,
    onSuccess: () => {
      UserNotification.success('Status tag updated');
      queryClient.invalidateQueries({
        queryKey: ['get-all-statuses'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-investigations'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-investigation-details'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { updateStatus: mutateAsync, updatingStatus: isLoading };
}

export function useDeleteStatus() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteStatus,
    onSuccess: () => {
      UserNotification.success('Status tag deleted');
      queryClient.invalidateQueries({
        queryKey: ['get-all-statuses'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-investigations'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-investigation-details'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { deleteStatus: mutateAsync, deletingStatus: isLoading };
}

export function useSetDefaultPriority() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: setDefaultPriority,
    onSuccess: () => {
      UserNotification.success('Default priority set');
      queryClient.invalidateQueries({
        queryKey: ['get-all-priorities'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { setDefault: mutateAsync, settingDefault: isLoading };
}

export function useRemoveDefaultPriority() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: removeDefaultPriority,
    onSuccess: () => {
      UserNotification.success('Default priority removed');
      queryClient.invalidateQueries({
        queryKey: ['get-all-priorities'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { removeDefault: mutateAsync, removingDefault: isLoading };
}

export function useSetDefaultStatus() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: setDefaultStatus,
    onSuccess: () => {
      UserNotification.success('Default status set');
      queryClient.invalidateQueries({
        queryKey: ['get-all-statuses'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { setDefault: mutateAsync, settingDefault: isLoading };
}

export function useRemoveDefaultStatus() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: removeDefaultStatus,
    onSuccess: () => {
      UserNotification.success('Default status removed');
      queryClient.invalidateQueries({
        queryKey: ['get-all-statuses'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { removeDefault: mutateAsync, removingDefault: isLoading };
}

export function useEnableNotifications() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: enableNotifications,
    onSuccess: (response: EnableNotificationsResponse) => {
      UserNotification.success(`${response.enabled ? 'Enabled' : 'Disabled'} investigation notifications`);
      queryClient.invalidateQueries({
        queryKey: ['get-investigations-config'],
      });
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return { enableNotifications: mutateAsync, enablingNotifications: isLoading };
}

export function useGetInvestigationsConfig() {
  const { data, isLoading } = useQuery({
    queryKey: ['get-investigations-config'],
    queryFn: () => defaultOnError(fetchInvestigationsConfig(), 'Error fetching investigations config'),
    retry: 2,
  });

  return {
    config:
      data ||
      ({
        enable_assignment_notifications: false,
        default_priority: '',
        default_status: '',
      } as ConfigApiType),
    loadingConfig: isLoading,
  };
}
